import {ReactElement} from 'react';
import {Box, Typography} from '@mui/material';
import {TFunction} from 'react-i18next';
import {Tray} from 'interfaces/Project';
import formatUnit from 'utils/formatUnit';

type Props = {
  tray: Tray;
  name: string;
  totalSection: number | null;
  t: TFunction;
};

export default function TraySummary({tray, name, totalSection, t}: Props): ReactElement {
  const specs = [
    {label: t('common:usefulSection'), value: tray.usefulSection, unit: 'millimeter'},
    {label: t('common:maxLoad'), value: tray.maxLoad, unit: 'kilogram-per-meter'},
    {label: t('common:totalSection'), value: totalSection, unit: 'millimeter', maximumFractionDigits: 2},
    {label: t('common:cablesWeight'), value: tray.cablesWeight, unit: 'kilogram', maximumFractionDigits: 2}
  ].filter(
    (spec): spec is {label: string; value: number; unit: string; maximumFractionDigits?: number} =>
      spec.value != null
  );

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5, containerType: 'inline-size'}}>
      <Box>
        <Typography sx={{fontSize: 18, fontWeight: 500, lineHeight: 1.5, color: 'text.primary'}}>
          {name}
        </Typography>
        <Typography sx={{fontSize: 14, fontWeight: 500, color: 'text.primary'}}>
          {t('common:referenceShort')}: {tray.clientReference}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          '@container (min-width: 480px)': {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 1,
            width: '100%',
            '@container (min-width: 480px)': {
              gridTemplateColumns: '1fr 1fr',
              width: 'auto',
              flex: '1 1 280px',
              maxWidth: 339,
              minWidth: 0
            }
          }}
        >
          {specs.map((spec) => (
            <Box key={spec.label} sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
              <Typography sx={{fontSize: 16, color: 'text.primary'}}>{spec.label}</Typography>
              <Typography sx={{fontSize: 16, fontWeight: 700, color: 'text.primary'}}>
                {formatUnit(spec.value, spec.unit, spec.maximumFractionDigits)}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box
          component="img"
          src={tray.image}
          alt={name}
          referrerPolicy="no-referrer"
          sx={{
            width: '100%',
            height: 'auto',
            objectFit: 'cover',
            borderRadius: '4px',
            flexShrink: 0,
            '@container (min-width: 480px)': {
              width: 240,
              height: 154
            }
          }}
        />
      </Box>
      {tray.description && (
        <Typography sx={{fontSize: 14, lineHeight: 1.5, color: 'text.primary'}}>
          {tray.description}
        </Typography>
      )}
    </Box>
  );
}
